import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import {
  PAGE_ENGAGEMENT_HEARTBEAT_INTERVAL,
  PageEngagementEvents,
  PageEngagementEventsWithProperties,
  PageEngagementProperties,
} from './events';

/**
 * Tracks how long a user actively engages with a page and sends periodic
 * `page_engagement_heartbeat` events to Segment.
 *
 * ## Single event type
 * All signals (mount, interval tick, final flush) use the same event so that
 * a single `MAX(active_time)` query per anonymous_id gives the total
 * engaged time without reconstructing sessions from multiple event types.
 *
 *   mount      → heartbeat { active_time: 0 }   ← bounce-detection anchor
 *   +30s       → heartbeat { active_time: 30 }
 *   tab hidden → (interval paused, no event)
 *   +20s back  → heartbeat { active_time: 50 }  ← resumes from 30, adds 20
 *   pagehide   → heartbeat { active_time: 67 }  ← final flush
 *
 * ## Active vs. total time
 * `active_time` only counts seconds where the page was BOTH visible AND had
 * OS focus. It pauses on:
 *   - Tab switch / window minimise  → `visibilitychange`
 *   - Cmd+Tab to another app        → `window.blur` (visibilitychange doesn't
 *                                      fire in this case)
 *
 * Self-contained: call `usePageEngagement()` with no arguments.
 */
export const usePageEngagement = () => {
  const { trackEvent } = useAnalytics<
    PageEngagementEvents,
    PageEngagementEventsWithProperties
  >();

  // ## Two-tier active-time accumulation
  //
  // Active time cannot be stored in a single incrementing counter because we
  // need to know the exact moment each "engaged window" ends. Instead we keep:
  //
  //   accumulatedActiveTimeRef  — sum of all CLOSED engagement windows (seconds)
  //   visibleSinceRef           — start timestamp of the CURRENT open window
  //
  // When deactivating: close the open window → accumulatedActiveTimeRef += elapsed
  // When computing:    total = accumulated + (now - visibleSinceRef)  [if engaged]
  //
  // Initialised to 0 / 0; actual values are set inside the setup effect to
  // satisfy the react-hooks/purity rule (Date.now() is impure at render time).
  const visibleSinceRef = useRef<number>(0);
  const accumulatedActiveTimeRef = useRef<number>(0);

  // Wall-clock mount time for computing time_on_page (includes hidden time).
  // Same purity reason — set inside the effect, not during render.
  const mountTimeRef = useRef<number>(0);

  // "Engaged" means the tab is visible AND the browser window has OS-level
  // focus. document.hidden alone misses Cmd+Tab — hasFocus() covers that gap.
  const isEngaged = useCallback(
    () => !document.hidden && document.hasFocus(),
    [],
  );

  // getProperties has empty deps and reads refs directly — refs are always
  // current so there is no stale-closure risk, and the callback never needs to
  // be recreated (important because sendHeartbeatRef avoids effect re-runs).
  const getProperties = useCallback((): PageEngagementProperties => {
    const now = Date.now();
    const timeOnPage = Math.round((now - mountTimeRef.current) / 1000);

    // If currently engaged, add the live open window on top of past windows.
    // If not engaged, visibleSinceRef is stale (from last activation) so we
    // use 0 — accumulatedActiveTimeRef already captured that window on deactivate.
    const currentWindowActive = isEngaged()
      ? Math.round((now - visibleSinceRef.current) / 1000)
      : 0;

    const activeTime = accumulatedActiveTimeRef.current + currentWindowActive;

    return {
      time_on_page: timeOnPage,
      active_time: activeTime,
      // Derived rather than tracked: avoids a third accumulator and is always
      // consistent with the other two fields.
      hidden_time: Math.max(0, timeOnPage - activeTime),
    };
  }, [isEngaged]);

  const sendHeartbeat = useCallback(() => {
    trackEvent(PageEngagementEvents.PageEngagementHeartbeat, getProperties());
  }, [trackEvent, getProperties]);

  // Stable ref accessed by the interval and close handlers so that all
  // callbacks always call the latest sendHeartbeat. Without this pattern the
  // useEffect dependency on sendHeartbeat would re-run the effect every time
  // Segment's async load() resolves and hands us a new trackEvent reference,
  // emitting a spurious second time=0 heartbeat.
  const sendHeartbeatRef = useRef(sendHeartbeat);
  sendHeartbeatRef.current = sendHeartbeat;

  useEffect(() => {
    // Set timestamps here (side effect) not at hook body level (render) to
    // comply with react-hooks/purity which bans impure calls like Date.now().
    const now = Date.now();
    mountTimeRef.current = now;
    visibleSinceRef.current = now;

    const heartbeatIntervalRef = {
      current: null as ReturnType<typeof setInterval> | null,
    };

    // Timestamp of the last heartbeat sent (any kind). Used in activate() to
    // detect a missed interval tick: if the user was away longer than the
    // interval, fire immediately on return rather than waiting another full cycle.
    let lastHeartbeatAt = now;

    // Wrap sendHeartbeatRef.current() so lastHeartbeatAt is always kept current.
    const sendAndRecord = () => {
      lastHeartbeatAt = Date.now();
      sendHeartbeatRef.current();
    };

    const startInterval = () => {
      if (heartbeatIntervalRef.current) return;
      heartbeatIntervalRef.current = setInterval(() => {
        // Secondary guard for the race between a scheduled tick and a
        // visibilitychange/blur that arrives in the same event-loop turn.
        // flushOnClose intentionally omits this guard — always flush on close.
        if (isEngaged()) {
          sendAndRecord();
        }
      }, PAGE_ENGAGEMENT_HEARTBEAT_INTERVAL);
    };

    const stopInterval = () => {
      if (!heartbeatIntervalRef.current) return;
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    };

    // isActive prevents redundant work when multiple events fire together.
    // Example: minimising the window triggers BOTH visibilitychange AND blur —
    // without this flag, deactivate() would double-add to accumulatedActiveTime.
    let isActive = isEngaged();

    // Separate from isActive: tracks whether the user has ever brought this tab
    // into focus. Used to defer the time=0 anchor heartbeat for pages that load
    // in a background tab (Cmd+click, session restore, etc.).
    let hasBecomeVisibleOnce = isActive;

    const deactivate = () => {
      if (!isActive) return;
      isActive = false;
      // Close the current engagement window by adding its duration to the
      // accumulator. visibleSinceRef was set on the last activate() call.
      accumulatedActiveTimeRef.current += Math.round(
        (Date.now() - visibleSinceRef.current) / 1000,
      );
      stopInterval();
    };

    const activate = () => {
      // Require BOTH visibility and focus — activate() is called from both
      // visibilitychange and focus events, so one condition may not hold yet.
      if (isActive || !isEngaged()) return;
      isActive = true;
      // Reset the open-window start time so getProperties() measures from now.
      visibleSinceRef.current = Date.now();

      if (!hasBecomeVisibleOnce) {
        // Page was loaded in the background. Fire the deferred anchor heartbeat
        // now that the user has actually looked at the page. active_time is 0
        // because no engaged time has passed yet; hidden_time reflects how long
        // the page was open before the user arrived.
        hasBecomeVisibleOnce = true;
        trackEvent(PageEngagementEvents.PageEngagementHeartbeat, {
          time_on_page: 0,
          active_time: 0,
          hidden_time: Math.round((Date.now() - mountTimeRef.current) / 1000),
        });
      }

      // If the user was away longer than one interval, they missed a scheduled
      // tick — fire immediately on return so the event stream never has a silent
      // gap longer than PAGE_ENGAGEMENT_HEARTBEAT_INTERVAL.
      // The interval then restarts from now, so the next tick is a full cycle away.
      if (Date.now() - lastHeartbeatAt >= PAGE_ENGAGEMENT_HEARTBEAT_INTERVAL) {
        sendAndRecord();
      }

      startInterval();
    };

    // visibilitychange covers: switching tabs, minimising the window, locking
    // the screen. It does NOT fire when Cmd+Tabbing to another app.
    const handleVisibilityChange = () =>
      document.hidden ? deactivate() : activate();

    // focus/blur cover the Cmd+Tab gap: the browser window loses OS focus while
    // the tab itself is still the "active" tab within Chrome.
    const handleBlur = () => deactivate();
    const handleFocus = () => activate();

    // Desktop browsers (Chrome, Firefox, Safari) fire BOTH pagehide AND
    // beforeunload when closing a tab or navigating away. hasFlushed prevents
    // the final heartbeat from being sent twice.
    let hasFlushed = false;
    const flushOnClose = () => {
      if (hasFlushed) return;
      hasFlushed = true;
      stopInterval();
      // Send regardless of engagement state — we always want the final flush
      // even if the user closed a backgrounded or blurred tab.
      sendHeartbeatRef.current();
    };

    // pagehide is preferred: fires on iOS Safari (which skips beforeunload)
    // and correctly covers bfcache navigations (the page isn't destroyed,
    // so beforeunload doesn't fire but pagehide does).
    // beforeunload is kept as belt-and-suspenders for older desktop browsers.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', flushOnClose);
    window.addEventListener('beforeunload', flushOnClose);

    if (isActive) {
      // Page loaded in a visible, focused tab: fire the time=0 anchor so that
      // bounces (user leaves before the 30s interval tick) are still recorded.
      trackEvent(PageEngagementEvents.PageEngagementHeartbeat, {
        time_on_page: 0,
        active_time: 0,
        hidden_time: 0,
      });
      startInterval();
    }
    // If not active at mount (background tab / no focus): activate() will fire
    // the deferred anchor heartbeat on the first engagement event.

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', flushOnClose);
      window.removeEventListener('beforeunload', flushOnClose);
    };
  // Empty deps: the effect runs exactly once. All callbacks that need the
  // latest trackEvent read from sendHeartbeatRef rather than closing over it,
  // avoiding re-runs that would emit duplicate heartbeats.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
